import "@mantine/core/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/notifications/styles.css";

import { MantineProvider, createTheme } from "@mantine/core";
import { Notifications, notifications } from "@mantine/notifications";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";

import { isNetworkError } from "./api/client";
import { CACHE_MAX_AGE_MS, persister } from "./lib/persist";
import { getToken, subscribe as subscribeToken } from "./lib/session";
import { router } from "./router";
import { reportConnection } from "./sync/engine";

const theme = createTheme({
  primaryColor: "teal",
  defaultRadius: "md",
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Agents change data in the background, so pages refetch whenever they're opened.
      staleTime: 0,
      // Keep data long enough for the saved copy to be useful offline.
      gcTime: CACHE_MAX_AGE_MS,
      // Always try the network (the browser's online flag is unreliable in tunnels) and
      // fall back to saved data when it fails.
      networkMode: "offlineFirst",
      retry: (count, error) => !isNetworkError(error) && count < 1,
      refetchOnWindowFocus: true,
    },
  },
  queryCache: new QueryCache({
    onSuccess: () => reportConnection(true),
    onError: (error) => isNetworkError(error) && reportConnection(false),
  }),
  mutationCache: new MutationCache({
    // Every failed save shows a message; forms don't need their own error handling.
    onError: (error, _vars, _ctx, mutation) => {
      if (isNetworkError(error)) reportConnection(false);
      notifications.show({
        color: "red",
        title: (mutation.meta?.errorTitle as string | undefined) ?? "Couldn't save",
        message: error.message,
      });
    },
  }),
});

// Signed out (or the session expired): forget this account's saved data.
subscribeToken(() => {
  if (getToken() === null) {
    queryClient.clear();
    void persister.removeClient();
  }
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="auto">
      {/* Bottom: within thumb reach on phones, e.g. for Undo. */}
      <Notifications position="bottom-center" />
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: CACHE_MAX_AGE_MS,
          buster: __API_VERSION__,
          dehydrateOptions: {
            // Keep data even when its last refresh failed (i.e. offline); by default only
            // queries whose last fetch succeeded are saved, which would empty the cache.
            shouldDehydrateQuery: (query) => query.state.data !== undefined,
          },
        }}
      >
        <RouterProvider router={router} />
      </PersistQueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);
